import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { filesAPI } from '../api';
import { FileRecord } from '../types/api';
import { useAuth } from '../contexts/AuthContext';

// Query keys
const QUERY_KEYS = {
  projectFiles: (projectId: string) => ['files', 'project', projectId] as const,
};

// Get files for a project
export function useProjectFiles(projectId: string) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: QUERY_KEYS.projectFiles(projectId),
    queryFn: async () => {
      const response = await filesAPI.getProjectFiles(projectId);
      return response.data?.files || [];
    },
    enabled: isAuthenticated && Boolean(projectId),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// File upload mutation (new format)
export function useUploadFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      file,
      autoProcess = false,
    }: {
      projectId: string;
      file: {
        filename: string;
        mimeType: string;
        fileSize: number;
      };
      autoProcess?: boolean;
    }) => {
      return await filesAPI.uploadFile(projectId, file, autoProcess);
    },
    onSuccess: (_, variables) => {
      // Invalidate project files query
      queryClient.invalidateQueries({ 
        queryKey: QUERY_KEYS.projectFiles(variables.projectId) 
      });
    },
  });
}

// Legacy file upload mutation (keep for backward compatibility)
export function useFileUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      file,
      onProgress,
    }: {
      projectId: string;
      file: File;
      onProgress?: (progress: number) => void;
    }) => {
      const uploadData = {
        filename: file.name,
        mimeType: file.type,
        fileSize: file.size,
      };
      
      const result = await filesAPI.uploadFile(projectId, uploadData, false);
      
      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }
      
      return result.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate project files query
      queryClient.invalidateQueries({ 
        queryKey: QUERY_KEYS.projectFiles(variables.projectId) 
      });
    },
  });
}

// File delete mutation
export function useDeleteFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileId: string) => filesAPI.deleteFile(fileId),
    onSuccess: (_, fileId) => {
      // Invalidate all project files queries
      queryClient.invalidateQueries({ 
        queryKey: ['files', 'project'] 
      });
    },
  });
}

// File processing mutation
export function useProcessFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileId: string) => filesAPI.processFile(fileId),
    onSuccess: (response, fileId) => {
      // Invalidate all project files queries to refresh status
      queryClient.invalidateQueries({ 
        queryKey: ['files', 'project'] 
      });

      // Also invalidate user data to refresh credits balance
      queryClient.invalidateQueries({
        queryKey: ['user']
      });
    },
  });
}

// File download mutation
export function useFileDownload() {
  return useMutation({
    mutationFn: async (fileId: string) => {
      const response = await filesAPI.getFileDownload(fileId);
      
      if (response.success && response.data) {
        // Open download URL in new tab
        window.open(response.data.downloadUrl, '_blank');
        return response.data;
      }
      
      throw new Error(response.message || 'Download failed');
    },
  });
}